'use client'

import { createClient } from './client'
import { timeAgoShort } from '@/lib/utils/dates'
import type { BookmarkSubject, SavedItem } from '@/types'

const supabase = createClient()

// Bookmarks are private (migration 046): owner-only at SELECT, INSERT and
// DELETE, no UPDATE policy and no UPDATE grant. There is no counter anywhere,
// so nothing can drift — which is why this is a plain table while voting had to
// become a SECURITY DEFINER RPC.
//
const bookmarks = () => supabase.from('bookmarks')

/** Discriminated on `ok` for the same reason as `VoteResult` in `forum.ts`. */
export type BookmarkResult =
  | { ok: true; saved: boolean }
  | { ok: false; error: string }

/**
 * Save or unsave. Returns the resulting state so a caller can settle an
 * optimistic update against the server rather than assuming its own guess held.
 */
export async function toggleBookmark(
  subjectType: BookmarkSubject,
  subjectId: string
): Promise<BookmarkResult> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return { ok: false, error: 'You need to be signed in to save things.' }
  const userId = session.user.id

  const { data: existing, error: readError } = await bookmarks()
    .select('subject_id')
    .eq('user_id', userId)
    .eq('subject_type', subjectType)
    .eq('subject_id', subjectId)
    .maybeSingle()

  // Destructured deliberately. A swallowed read error here would make an
  // existing bookmark look absent and the insert would then fail on the primary
  // key — the exact class of silent failure `events.ts` was full of.
  if (readError) return { ok: false, error: readError.message }

  if (existing) {
    const { error } = await bookmarks()
      .delete()
      .eq('user_id', userId)
      .eq('subject_type', subjectType)
      .eq('subject_id', subjectId)
    return error ? { ok: false, error: error.message } : { ok: true, saved: false }
  }

  const { error } = await bookmarks()
    .insert({ user_id: userId, subject_type: subjectType, subject_id: subjectId })
  return error ? { ok: false, error: error.message } : { ok: true, saved: true }
}

/**
 * Which of these subjects the viewer has saved. Pass `ids` when decorating a
 * list; omit it to get every saved id of that type.
 */
export async function getMyBookmarks(
  subjectType: BookmarkSubject,
  ids?: string[]
): Promise<Set<string>> {
  if (ids && ids.length === 0) return new Set()

  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return new Set()

  let query = bookmarks()
    .select('subject_id')
    .eq('user_id', session.user.id)
    .eq('subject_type', subjectType)
  if (ids) query = query.in('subject_id', ids)

  const { data, error } = await query
  if (error) {
    console.warn('[getMyBookmarks] Error:', error.message)
    return new Set()
  }
  return new Set(((data ?? []) as { subject_id: string }[]).map((r) => r.subject_id))
}

/**
 * Every saved item, hydrated for display and newest-saved first.
 *
 * Shaped like `getUserContinueItems()` in `dashboard.ts`, which already unions
 * heterogeneous content into one list — same idea, same fields, so `/saved` and
 * the dashboard section can share a renderer.
 *
 * A bookmark carries no FK to its subject (the column addresses three tables),
 * so a saved item whose subject has since been deleted simply does not come
 * back from the hydrating query. That is the intended behaviour: it disappears
 * from the list rather than rendering as a broken row.
 */
export async function getSavedItems(limit?: number): Promise<SavedItem[]> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) return []

  const { data, error } = await bookmarks()
    .select('subject_type, subject_id, created_at')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false })

  if (error) {
    console.warn('[getSavedItems] Error:', error.message)
    return []
  }

  const rows = (data ?? []) as { subject_type: BookmarkSubject; subject_id: string; created_at: string }[]
  if (rows.length === 0) return []

  const savedAt = new Map(rows.map((r) => [`${r.subject_type}:${r.subject_id}`, r.created_at]))
  const idsOf = (t: BookmarkSubject) => rows.filter((r) => r.subject_type === t).map((r) => r.subject_id)

  const [threadIds, jobIds, articleIds] = [idsOf('thread'), idsOf('job'), idsOf('article')]

  // Three queries, not one per bookmark. Each is skipped entirely when nothing
  // of that type is saved.
  const [threads, jobs, articles] = await Promise.all([
    threadIds.length
      ? supabase.from('forum_threads').select('id, title, reply_count').in('id', threadIds)
      : Promise.resolve({ data: [], error: null }),
    jobIds.length
      ? supabase
          .from('jobs')
          .select('id, title, is_active, company:companies!jobs_company_id_fkey(name)')
          .in('id', jobIds)
      : Promise.resolve({ data: [], error: null }),
    articleIds.length
      ? supabase.from('articles').select('id, title, published_at, created_at').in('id', articleIds)
      : Promise.resolve({ data: [], error: null }),
  ])

  const items: SavedItem[] = []

  for (const t of (threads.data ?? []) as { id: string; title: string; reply_count: number }[]) {
    items.push({
      type: 'thread',
      id: t.id,
      title: t.title,
      link: `/forum/${t.id}`,
      meta: `${t.reply_count} ${t.reply_count === 1 ? 'reply' : 'replies'}`,
      savedAt: savedAt.get(`thread:${t.id}`)!,
    })
  }

  // The embed comes back as an array or an object depending on the join —
  // `members.ts` and `dashboard.ts` both do this dance. Normalise once.
  type JobRow = { id: string; title: string; is_active: boolean; company: { name: string } | { name: string }[] | null }
  for (const j of (jobs.data ?? []) as JobRow[]) {
    const company = Array.isArray(j.company) ? j.company[0] : j.company
    items.push({
      type: 'job',
      id: j.id,
      title: j.title,
      link: `/jobs/${j.id}`,
      // A saved job that has since closed still shows — knowing it closed is
      // more useful than it vanishing without explanation.
      meta: j.is_active ? (company?.name ?? 'Job') : 'No longer accepting applications',
      savedAt: savedAt.get(`job:${j.id}`)!,
    })
  }

  // The articles library has no read-time column, and the list surface shows a
  // relative publish date — match it rather than inventing a second convention.
  type ArticleRow = { id: string; title: string; published_at: string | null; created_at: string }
  for (const a of (articles.data ?? []) as ArticleRow[]) {
    items.push({
      type: 'article',
      id: a.id,
      title: a.title,
      link: `/articles/${a.id}`,
      meta: timeAgoShort(a.published_at ?? a.created_at),
      savedAt: savedAt.get(`article:${a.id}`)!,
    })
  }

  items.sort((a, b) => b.savedAt.localeCompare(a.savedAt))
  return limit ? items.slice(0, limit) : items
}
